import React from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Typography, Box, Paper, Stack } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

interface FallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

const ErrorFallback: React.FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleGoBack = () => {
    // Use window.history to ensure proper browser navigation
    // resetKeys will handle resetting the error boundary when location changes
    if (window.history.length > 1) {
      window.history.back();
    } else {
      // No history to go back to, go home instead
      resetErrorBoundary();
      navigate('/');
    }
  };

  const handleGoHome = () => {
    resetErrorBoundary();
    navigate('/');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: 3,
        backgroundColor: '#f5f5f5'
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 500,
          textAlign: 'center'
        }}
      >
        <ErrorOutlineIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          {t('error-boundary.title', 'Something went wrong')}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {error.message}
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
          >
            {t('error-boundary.go-back', 'Go back')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={resetErrorBoundary}
          >
            {t('error-boundary.try-again', 'Try again')}
          </Button>
          <Button
            variant="text"
            color="primary"
            onClick={handleGoHome}
          >
            {t('error-boundary.go-home', 'Home')}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

interface ErrorBoundaryProps {
  children: React.ReactNode;
  onReset?: () => void;
  onError?: (error: Error, info: { componentStack: string }) => void;
}

/**
 * Location-aware ErrorBoundary that resets when the user navigates.
 * Must be rendered inside <BrowserRouter> to access useLocation().
 */
export const LocationAwareErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  children,
  onReset,
  onError
}) => {
  const location = useLocation();

  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={onReset}
      onError={onError}
      resetKeys={[location.pathname]}
    >
      {children}
    </ReactErrorBoundary>
  );
};

/**
 * Basic ErrorBoundary without location awareness.
 * Use outside of <BrowserRouter> (e.g. as the outermost wrapper).
 */
export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  children,
  onReset,
  onError
}) => {
  return (
    <ReactErrorBoundary
      FallbackComponent={BasicErrorFallback}
      onReset={onReset}
      onError={onError}
    >
      {children}
    </ReactErrorBoundary>
  );
};

/**
 * Minimal fallback for use outside Router context (no navigate).
 * Translates via the global i18next instance instead of useTranslation():
 * this boundary is the outermost wrapper with no Suspense above it, so the
 * hook's suspense behavior could crash the fallback while i18n is loading.
 */
const translateSafely = (key: string, fallback: string): string => {
  try {
    return i18n.isInitialized ? i18n.t(key, fallback) : fallback;
  } catch {
    return fallback;
  }
};

const BasicErrorFallback: React.FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: 3,
        backgroundColor: '#f5f5f5'
      }}
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
        <ErrorOutlineIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          {translateSafely('error-boundary.title', 'Something went wrong')}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {error.message}
        </Typography>
        <Button variant="contained" color="primary" onClick={resetErrorBoundary} sx={{ mt: 2 }}>
          {translateSafely('error-boundary.try-again', 'Try again')}
        </Button>
      </Paper>
    </Box>
  );
};
